import CircleIcon from "@mui/icons-material/Circle";
import { Box, List, ListItemButton, Typography } from "@mui/material";
import React, { useState, MouseEvent } from "react";
import { ICalendarTaskItem } from "ui/Scripts/source/models/calendarTask/calendarTaskItem";
import { Base } from "ui/Scripts/source/framework/base";
import { Translations } from "ui/Scripts/source/models/translations";
import { EllipsisTypography } from "../../../framework/Typography/EllipsisTypography";
import { ListLinkValue } from "source/components/framework/List/ListLinkValue";
import { CalendarTaskState } from "ui/Scripts/source/models/enums";
import { ListStateIconSuccess } from "source/components/framework/List/ListStateIconSuccess";
import { ListStateIconStarted } from "source/components/framework/List/ListStateIconStarted";
import { ListStateIconNotStarted } from "source/components/framework/List/ListStateIconNotStarted";
import { ToolButtonDropdownMenu } from "ui/Scripts/source/components/framework/Button/ToolButtonDropdownMenu";
import { ListStateIconPrivate } from "source/components/framework/List/ListStateIconPrivate";
import { ListStateIconNotification } from "source/components/framework/List/ListStateIconNotification";
import { GetCalendarTaskReminderTitle } from "../../CalendarTaskPage/CalendarTaskPageUtils";

export interface ICalendarTaskDayItemItem extends ICalendarTaskItem {
    startsEarlierDate: boolean;
    endsLaterDate: boolean;
}

export interface ICalendarTaskDayItem {
    id: string;
    dayNumber: string;
    dayLabel: string;
    dateLabel: string;
    events: ICalendarTaskDayItemItem[];
}

interface CalendarTaskListItemProps {
    item: ICalendarTaskDayItem;
    selectedId?: string;
    onClick?: (id: string) => void;
    onOpen: (id: string, isSeries: boolean) => void;
}

const getTimeStr = (event: ICalendarTaskDayItemItem) => {
    if (event.allDay) {
        return Translations.AllDay;
    }
    if (!event.endDate) {
        return Base.timeToTimeStr(event.startDate);
    }
    if (event.startsEarlierDate) {
        return `- ${Base.timeToTimeStr(event.endDate)}`;
    }
    if (event.endsLaterDate) {
        return `${Base.timeToTimeStr(event.startDate)} - `;
    }
    return `${Base.timeToTimeStr(event.startDate)} - ${Base.timeToTimeStr(event.endDate)}`;
};

export const CalendarTaskListItem = (props: CalendarTaskListItemProps) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const getActions = (parentId: string, id: string) => [
        { title: Translations.CalendarTaskEditOccurrence, onClick: () => { props.onOpen(id, false); } },
        { title: Translations.CalendarTaskEditSeries, onClick: () => { props.onOpen(parentId, true); } },
    ];

    const handleToggleClick = (event: MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const getStateIcon = (state: CalendarTaskState): React.ElementType => {
        if (state === CalendarTaskState.Finished) return ListStateIconSuccess;
        if (state === CalendarTaskState.Started) return ListStateIconStarted;
        return ListStateIconNotStarted;
    };

    const isYearTitle = props.item?.id.includes("year-");
    const isWeekTitle = !isYearTitle && props.item?.id.includes("week-");

    return (
        <Box
            sx={{
                display: "flex",
                borderBottom: "1px solid",
                borderColor: "divider",
                margin: "0 1rem",
                padding: ".25rem 0",
            }}
        >
            {isYearTitle
                ? (
                    <Typography variant="subtitle1" sx={{ color: "primary.main" }}>
                        {props.item?.dayNumber.toString()}
                    </Typography>
                )
                : (isWeekTitle
                    ? (
                        <Typography variant="subtitle2" sx={{ color: "primary.main" }}>
                            {`${Translations.Week} ${props.item?.dayNumber}`}
                        </Typography>
                    )
                    : (
                        <>
                            <Typography
                                variant="h4"
                                sx={{
                                    width: "50px",
                                    margin: "0 1.5rem 0 .75rem",
                                    textAlign: "right",
                                    color: "text.secondary"
                                }}
                            >
                                {props.item?.dayNumber}
                            </Typography>
                            <Box sx={{ display: "flex", flexDirection: "column", minWidth: "150px" }}>
                                <Typography
                                    variant="subtitle2"
                                    sx={{ lineHeight: "1", marginTop: ".3125rem", color: "text.secondary" }}
                                >
                                    {props.item?.dayLabel}
                                </Typography>
                                <Typography variant="subtitle2" sx={{ lineHeight: "1", color: "text.secondary" }}>
                                    {props.item?.dateLabel}
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    marginLeft: "1rem",
                                    flexGrow: 2,
                                    maxWidth: "calc(100% - 262px)",
                                    overflow: "hidden",
                                }}
                            >
                                <List sx={{ margin: 0, padding: 0 }}>
                                    {props.item?.events.map((calendarTask) => {
                                        const StateIcon = getStateIcon(calendarTask.state);
                                        return (
                                            <ListItemButton
                                                key={calendarTask.id}
                                                selected={props.selectedId === calendarTask.id}
                                                sx={{ paddingLeft: 0, paddingRight: 0 }}
                                                onClick={props.onClick ? () => props.onClick(calendarTask.id) : null}
                                            >
                                                <Box sx={{ display: "flex", flexGrow: 1, width: "100%" }}>
                                                    <Typography component="span" sx={{ width: "40%", display: "flex", alignItems: "center" }}>
                                                        <Box sx={{ display: "flex", alignItems: "center", minWidth: "75px" }}>
                                                            <StateIcon
                                                                tooltip={calendarTask.state === CalendarTaskState.Finished
                                                                    ? Translations.CalendarTaskStateFinished
                                                                    : calendarTask.state === CalendarTaskState.Started
                                                                        ? Translations.CalendarTaskStateStarted
                                                                        : Translations.CalendarTaskStateNotStarted
                                                                }
                                                            />
                                                            {calendarTask.isPrivate &&
                                                                <ListStateIconPrivate />
                                                            }
                                                            {calendarTask.remainder >= 0 &&
                                                                <ListStateIconNotification tooltip={`${Translations.Reminder}: ${GetCalendarTaskReminderTitle(calendarTask.remainder.toString(10))}`} />
                                                            }
                                                        </Box>
                                                        <Box sx={{ marginLeft: "10px" }}>{getTimeStr(calendarTask)}</Box>
                                                    </Typography>
                                                    <Box sx={{ display: "flex", alignItems: "center", width: "60%" }}>
                                                        <CircleIcon
                                                            sx={{
                                                                margin: "0 .5rem",
                                                                fontSize: "12px",
                                                                color: calendarTask.categoryColor,
                                                            }}
                                                        />
                                                        <EllipsisTypography>
                                                            <ListLinkValue onClick={calendarTask.parentId ? handleToggleClick : () => props.onOpen(calendarTask.id, false)}>{calendarTask.title}</ListLinkValue>
                                                        </EllipsisTypography>
                                                    </Box>
                                                </Box>
                                                <ToolButtonDropdownMenu
                                                    anchorEl={anchorEl}
                                                    actions={getActions(calendarTask.parentId, calendarTask.id)}
                                                    onClose={() => setAnchorEl(null)}
                                                />
                                            </ListItemButton>
                                        );
                                    })}
                                </List>
                            </Box>
                        </>
                    )
                )}
        </Box>
    );
};
